import { useCallback, useEffect, useRef, useState } from 'react';

export type HistoryItem = {
  id: number;
  endpoint: string;
  model_name: string;
  job_status: 'pending' | 'processing' | 'completed' | 'failed';
  success: boolean;
  credits_used: number;
  promo_credits_used?: number;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  external_request_id: string | null;
  // The batch.pipelet.net job (and queue) behind the gateway request, once the
  // poller has learned it from the gateway's status. This is the id that
  // batch_jobs, job_events and the worker logs use.
  batch_job_id?: string | null;
  batch_queue?: string | null;
  metadata: { r2_key?: string; r2_keys?: string[]; thumbnail_r2_key?: string; request_id?: string; batch_job_id?: string; batch_queue?: string; input?: Record<string, unknown> } | null;
  // Prompt enrichment: raw user prompt vs. the enriched prompt submitted to the
  // model. Present only for endpoints that run enrichment (e.g. ltx-video).
  original_prompt?: string | null;
  enriched_prompt?: string | null;
};

export const HISTORY_PAGE_SIZE = 50;

export function useHistory(opts: { endpoint?: string; pollMs?: number; pageSize?: number } = {}) {
  const pageSize = opts.pageSize ?? HISTORY_PAGE_SIZE;
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // zero-based
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const optsRef = useRef({ ...opts, pageSize });
  optsRef.current = { ...opts, pageSize };
  const pageRef = useRef(page);
  pageRef.current = page;

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { endpoint, pageSize: size } = optsRef.current;
      const params = new URLSearchParams({
        limit: String(size),
        offset: String(pageRef.current * size),
      });
      if (endpoint) params.set('endpoint', endpoint);
      const res = await fetch(`/api/v1/history?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`history fetch failed: ${res.status}`);
      const data = await res.json();
      setItems(data.items as HistoryItem[]);
      setTotal(Number(data.total ?? (data.items as HistoryItem[]).length));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOnce();
    const ms = opts.pollMs ?? 0;
    if (ms > 0) {
      const id = setInterval(() => void fetchOnce(), ms);
      return () => clearInterval(id);
    }
  }, [fetchOnce, opts.pollMs, page]);

  const remove = useCallback(
    async (id: number) => {
      const res = await fetch(`/api/v1/history/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((n) => Math.max(0, n - 1));
      // Backfill the gap from the next page so the grid stays full.
      void fetchOnce();
    },
    [fetchOnce]
  );

  const retry = useCallback(async (id: number): Promise<{ activity_id: number; request_id: string }> => {
    const res = await fetch(`/api/v1/history/${id}/retry`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = (data && (data.error || data.message)) || `retry failed: ${res.status}`;
      const err = new Error(typeof message === 'string' ? message : 'retry failed') as Error & {
        status?: number;
        code?: string;
      };
      err.status = res.status;
      err.code = data?.error;
      throw err;
    }
    return data as { activity_id: number; request_id: string };
  }, []);

  const presignedUrl = useCallback(
    async (id: number, r2Key?: string): Promise<{ url: string; urls: string[]; thumbnailUrl?: string }> => {
      const res = await fetch(`/api/v1/history/${id}/presigned-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(r2Key ? { r2_key: r2Key } : {}),
      });
      if (!res.ok) throw new Error(`presigned-url failed: ${res.status}`);
      const data = await res.json();
      const url = data.video_url as string;
      const urls = (Array.isArray(data.urls) ? data.urls : [url]) as string[];
      const thumbnailUrl = typeof data.thumbnail_url === 'string' ? data.thumbnail_url : undefined;
      return { url, urls, thumbnailUrl };
    },
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // If the page count shrinks (e.g. after deletes), keep page in range.
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  return {
    items,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    loading,
    error,
    refresh: fetchOnce,
    remove,
    presignedUrl,
    retry,
  };
}
